import { create } from "zustand";
import firebase from "@services/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
} from "firebase/firestore";
import { notificationsApi } from "@services/api/notifications";
import { useMessagerieStore } from "./messagerieStore";

export type InAppEventType =
  | "payment_received"
  | "booking_created"
  | "document_signed"
  | "new_message"
  | "devis_signed"
  | "ordre_status_changed"
  | "avis_received"
  | "devis_auto_cancelled"
  | "devis_no_response"
  | "mention";

export type InAppNotification = {
  id: string;
  type: InAppEventType;
  title: string;
  body: string;
  href: string;
  read: boolean;
  createdAt: string;
};

type InAppNotificationsState = {
  notifications: InAppNotification[];
  _initialized: boolean;
  _userId: string | null;
  _unsub: (() => void) | null;

  /** Fetch notifications from API and start Firestore real-time listener. */
  init: (userId: string) => void;

  /** Stop the Firestore listener. */
  cleanup: () => void;

  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
};

// Les notifications de message ne vont PAS dans la cloche : elles alimentent le
// compteur de la messagerie. Deux fils distincts, un seul incrément par message.
// Le backend n'enregistre pas de champ discussion_id, mais le href le porte
// (/messagerie?tab=...&discussion=<id>).
function discussionIdFromHref(href?: string): string | null {
  const m = /[?&]discussion=([^&]+)/.exec(href || "");
  return m ? decodeURIComponent(m[1]) : null;
}

function routeMessageNotification(doc: { href?: string }): void {
  const discId = discussionIdFromHref(doc.href);
  if (!discId) return;
  const ms = useMessagerieStore.getState();
  // Pas de badge sur la conversation qu'on est en train de lire.
  if (ms.viewingDiscussionId === discId) return;
  useMessagerieStore.setState((st) => ({
    unreadCounts: { ...st.unreadCounts, [discId]: (st.unreadCounts[discId] || 0) + 1 },
    discussions: st.discussions.map((d) => (d.id === discId ? { ...d, unread: true } : d)),
  }));
}

export const useInAppNotificationsStore = create<InAppNotificationsState>()(
  (set, get) => ({
    notifications: [],
    _initialized: false,
    _userId: null,
    _unsub: null,

    init: (userId: string) => {
      if (!userId) return;
      // Same user, already listening → nothing to do. A DIFFERENT user means
      // someone signed out and back in on the same tab: this store is a module
      // singleton, so the previous user's listener and notifications would leak
      // into the new session. Tear down first.
      if (get()._initialized) {
        if (get()._userId === userId) return;
        get().cleanup();
      }
      set({ _initialized: true, _userId: userId });

      // Fetch existing notifications from API
      notificationsApi
        .fetchAll()
        .then((notifications) =>
          set({ notifications: notifications.filter((n) => n.type !== "new_message") }),
        )
        .catch((err) =>
          console.warn("Failed to fetch notifications:", err),
        );

      // Real-time listener for new notifications
      const notifQuery = query(
        collection(firebase.db, "user_notifications"),
        where("user_id", "==", userId),
        orderBy("created_at", "desc"),
        // Window of 20, not 1: with a single-document window, two notifications
        // arriving between two snapshots leave only the newest in the query, so
        // the older one never shows up as an "added" change and is lost.
        fsLimit(20),
      );

      let initialLoad = true;
      const unsub = onSnapshot(
        notifQuery,
        (snapshot) => {
          if (initialLoad) {
            initialLoad = false;
            return;
          }
          for (const change of snapshot.docChanges()) {
            if (change.type === "modified") {
              // Ex. avis client corrigé : le serveur réécrit la notification existante.
              const { body } = change.doc.data();
              set((s) => ({
                notifications: s.notifications.map((n) =>
                  n.id === change.doc.id && body ? { ...n, body } : n,
                ),
              }));
              continue;
            }
            if (change.type !== "added") continue;
            const doc = change.doc.data();
            if (doc.type === "new_message") {
              routeMessageNotification(doc);
              continue;
            }
            const notif: InAppNotification = {
              id: change.doc.id,
              type: doc.type as InAppEventType,
              title: doc.title || "",
              body: doc.body || "",
              href: doc.href || "/",
              read: doc.read ?? false,
              createdAt: doc.created_at || new Date().toISOString(),
            };
            // Avoid duplicates (may already be in the API-fetched list)
            set((s) => {
              if (s.notifications.some((n) => n.id === notif.id)) return s;
              return {
                notifications: [notif, ...s.notifications].slice(0, 50),
              };
            });
          }
        },
        (err) => {
          console.warn("User notifications Firestore listener error:", err);
        },
      );

      set({ _unsub: unsub });
    },

    cleanup: () => {
      const unsub = get()._unsub;
      if (unsub) unsub();
      set({ _unsub: null, _initialized: false, _userId: null, notifications: [] });
    },

    markRead: (id) => {
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n,
        ),
      }));
      notificationsApi.markRead(id).catch((err) =>
        console.warn("Failed to mark notification read:", err),
      );
    },

    markAllRead: () => {
      set((s) => ({
        notifications: s.notifications.map((n) => ({ ...n, read: true })),
      }));
      notificationsApi.markAllRead().catch((err) =>
        console.warn("Failed to mark all notifications read:", err),
      );
    },

    dismiss: (id) => {
      set((s) => ({
        notifications: s.notifications.filter((n) => n.id !== id),
      }));
      notificationsApi.dismiss(id).catch((err) =>
        console.warn("Failed to dismiss notification:", err),
      );
    },

    clearAll: () => {
      set({ notifications: [] });
      notificationsApi.clearAll().catch((err) =>
        console.warn("Failed to clear all notifications:", err),
      );
    },
  }),
);
